import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useMenuSettings } from '../../hooks/useMenuSettings';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();

  // Use menu settings hook to control visibility
  const {
    isShopVisible,
    isGalleryVisible,
    isResourcesVisible,
    isServicesVisible,
    isContactVisible,
    isAboutVisible,
    loading: menuSettingsLoading
  } = useMenuSettings();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Services dropdown items
  const serviceTypes = [
    { label: 'House Cleaning', path: '/service-details/house-cleaning', icon: 'Home' },
    { label: 'Office Cleaning', path: '/service-details/office-cleaning', icon: 'Building' },
    { label: 'Carpet Cleaning', path: '/service-details/carpet-cleaning', icon: 'Square' },
    { label: 'Deep Cleaning', path: '/service-details/deep-cleaning', icon: 'Sparkles' },
    { label: 'Post-Construction', path: '/service-details/post-construction', icon: 'HardHat' },
    { label: 'Disinfection', path: '/service-details/disinfection', icon: 'Shield' },
    { label: 'Flooring Services', path: '/service-details/flooring', icon: 'Grid3x3' },
  ];

  // User/Admin menu items (moved from more menu)
  const userMenuItems = [
    { label: 'Customer Portal', path: '/customer-portal', icon: 'User' },
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Settings' },
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path || (path === '/homepage' && location?.pathname === '/');
  };

  const isServicesActive = () => {
    return serviceTypes?.some(service => location?.pathname?.startsWith(service?.path)) || 
           location?.pathname === '/service-details' || 
           location?.pathname === '/services';
  };

  return (
    <>
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-sm shadow-elevation-2' 
            : 'bg-white'
        }`}
      >
        <div className="w-full">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Logo */}
            <Link 
              to="/homepage" 
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200"
              onClick={closeMobileMenu}
            >
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Sparkles" size={20} color="white" />
              </div>
              <span className="text-xl font-semibold text-text-primary">
                Clean360
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {/* Home - Always visible */}
              <Link
                to="/homepage"
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActivePath('/homepage')
                    ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name="Home" size={16} />
                <span>Home</span>
              </Link>
              
              {/* About Us - Conditionally visible */}
              {isAboutVisible && (
                <Link
                  to="/about-us"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath('/about-us')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="Users" size={16} />
                  <span>About Us</span>
                </Link>
              )}

              {/* Services Dropdown - Conditionally visible */}
              {isServicesVisible && (
                <div className="relative">
                  <Link
                    to="/services"
                    onMouseEnter={() => setShowServicesDropdown(true)}
                    onMouseLeave={() => setShowServicesDropdown(false)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isServicesActive()
                        ? 'bg-primary text-white shadow-elevation-1'
                        : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                    }`}
                  >
                    <Icon name="Wrench" size={16} />
                    <span>Services</span>
                    <Icon name="ChevronDown" size={14} />
                  </Link>
                  
                  {/* Services Dropdown Menu */}
                  {showServicesDropdown && (
                    <div 
                      className="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-elevation-3 border border-border py-2 z-50"
                      onMouseEnter={() => setShowServicesDropdown(true)}
                      onMouseLeave={() => setShowServicesDropdown(false)}
                    >
                      <Link
                        to="/services"
                        className="flex items-center space-x-3 px-4 py-3 text-sm font-medium text-primary border-b border-border mb-2"
                      >
                        <Icon name="Grid3x3" size={16} />
                        <span>All Services</span>
                      </Link>
                      {serviceTypes?.map((service) => (
                        <Link
                          key={service?.path}
                          to={service?.path}
                          className={`flex items-center space-x-3 px-4 py-3 text-sm transition-colors duration-200 ${
                            isActivePath(service?.path)
                              ? 'bg-primary/10 text-primary font-medium' : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                          }`}
                        >
                          <Icon name={service?.icon} size={16} />
                          <span>{service?.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Contact Us - Conditionally visible */}
              {isContactVisible && (
                <Link
                  to="/smart-contact"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath('/smart-contact')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="MessageCircle" size={16} />
                  <span>Contact Us</span>
                </Link>
              )}
              
              {/* Resources - Conditionally visible */}
              {isResourcesVisible && (
                <Link
                  to="/resources-hub"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath('/resources-hub')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="BookOpen" size={16} />
                  <span>Resources</span>
                </Link>
              )}
              
              {/* Gallery - Conditionally visible */}
              {isGalleryVisible && (
                <Link
                  to="/projects-gallery"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath('/projects-gallery')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="Image" size={16} />
                  <span>Gallery</span>
                </Link>
              )}
              
              {/* Shop - Conditionally visible */}
              {isShopVisible && (
                <Link
                  to="/e-commerce-store"
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActivePath('/e-commerce-store')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="ShoppingBag" size={16} />
                  <span>Shop</span>
                </Link>
              )}
            </nav>

            {/* Desktop Right Side - CTA + User Menu */}
            <div className="hidden lg:flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Phone"
                iconPosition="left"
                iconSize={16}
              >
                Call Now
              </Button>
              <Button
                variant="default"
                size="sm"
                iconName="Calendar"
                iconPosition="left"
                iconSize={16}
              >
                Book Service
              </Button>
              
              {/* User Menu */}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="text-text-secondary hover:text-text-primary"
                  aria-label="User menu"
                >
                  <Icon name="UserCircle" size={20} />
                </Button>
                
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-elevation-3 border border-border py-2 z-50">
                    {userMenuItems?.map((item) => (
                      <Link
                        key={item?.path}
                        to={item?.path}
                        onClick={() => setShowUserMenu(false)}
                        className={`flex items-center space-x-3 px-4 py-2 text-sm transition-colors duration-200 ${
                          isActivePath(item?.path)
                            ? 'bg-primary/10 text-primary font-medium' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                        }`}
                      >
                        <Icon name={item?.icon} size={16} />
                        <span>{item?.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
              />
            </Button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeMobileMenu}
          />
        )}

        {/* Mobile Menu */}
        <div 
          className={`fixed top-16 left-0 right-0 bg-white border-t border-border z-40 lg:hidden transition-all duration-300 ${
            isMobileMenuOpen 
              ? 'opacity-100 translate-y-0' :'opacity-0 -translate-y-4 pointer-events-none'
          }`}
        >
          <div className="px-4 py-6 space-y-4">
            {/* Primary Navigation */}
            <div className="space-y-2">
              {/* Home - Always visible */}
              <Link
                to="/homepage"
                onClick={closeMobileMenu}
                className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                  isActivePath('/homepage')
                    ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                }`}
              >
                <Icon name="Home" size={20} />
                <span>Home</span>
              </Link>
              
              {/* About Us - Conditionally visible */}
              {isAboutVisible && (
                <Link
                  to="/about-us"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActivePath('/about-us')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="Users" size={20} />
                  <span>About Us</span>
                </Link>
              )}

              {/* Mobile Services Section - Conditionally visible */}
              {isServicesVisible && (
                <div className="space-y-2">
                  <Link
                    to="/services"
                    onClick={closeMobileMenu}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                      isActivePath('/services')
                        ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                    }`}
                  >
                    <Icon name="Wrench" size={20} />
                    <span>Services</span>
                  </Link>
                  <div className="px-4 py-2 text-xs font-semibold text-text-primary uppercase tracking-wider">
                    Service Types
                  </div>
                  {serviceTypes?.map((service) => (
                    <Link
                      key={service?.path}
                      to={service?.path}
                      onClick={closeMobileMenu}
                      className={`flex items-center space-x-3 px-4 py-3 ml-4 rounded-lg text-base transition-all duration-200 ${
                        isActivePath(service?.path)
                          ? 'bg-primary/10 text-primary font-medium' : 'text-text-secondary hover:text-text-primary hover:bg-muted'
                      }`}
                    >
                      <Icon name={service?.icon} size={18} />
                      <span>{service?.label}</span>
                    </Link>
                  ))}
                </div>
              )}

              {/* Contact Us - Conditionally visible */}
              {isContactVisible && (
                <Link
                  to="/smart-contact"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActivePath('/smart-contact')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="MessageCircle" size={20} />
                  <span>Contact Us</span>
                </Link>
              )}
              
              {/* Resources - Conditionally visible */}
              {isResourcesVisible && (
                <Link
                  to="/resources-hub"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActivePath('/resources-hub')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="BookOpen" size={20} />
                  <span>Resources</span>
                </Link>
              )}
              
              {/* Gallery - Conditionally visible */}
              {isGalleryVisible && (
                <Link
                  to="/projects-gallery"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActivePath('/projects-gallery')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="Image" size={20} />
                  <span>Gallery</span>
                </Link>
              )}
              
              {/* Shop - Conditionally visible */}
              {isShopVisible && (
                <Link
                  to="/e-commerce-store"
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 ${
                    isActivePath('/e-commerce-store')
                      ? 'bg-primary text-white shadow-elevation-1' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name="ShoppingBag" size={20} />
                  <span>Shop</span>
                </Link>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-border my-4" />

            {/* User/Admin Navigation */}
            <div className="space-y-2">
              <div className="px-4 py-2 text-xs font-semibold text-text-primary uppercase tracking-wider">
                User Area
              </div>
              {userMenuItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base transition-all duration-200 ${
                    isActivePath(item?.path)
                      ? 'bg-primary/10 text-primary font-medium' :'text-text-secondary hover:text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
            </div>

            {/* Loading indicator for menu settings */}
            {menuSettingsLoading && (
              <div className="text-center py-4">
                <div className="text-sm text-text-secondary">Loading menu...</div>
              </div>
            )}

            {/* Mobile CTA Buttons */}
            <div className="pt-4 space-y-3">
              <Button
                variant="outline"
                fullWidth
                iconName="Phone"
                iconPosition="left"
                iconSize={18}
                onClick={closeMobileMenu}
              >
                Call Now
              </Button>
              <Button
                variant="default"
                fullWidth
                iconName="Calendar"
                iconPosition="left"
                iconSize={18}
                onClick={closeMobileMenu}
              >
                Book Service
              </Button>
            </div>
          </div>
        </div>
      </header>
      {/* Click outside handlers */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setShowUserMenu(false)}
        />
      )}
      {showServicesDropdown && (
        <div 
          className="fixed inset-0 z-30"
          onClick={() => setShowServicesDropdown(false)}
        />
      )}
    </>
  );
};

export default Header;